import React, { memo, useReducer } from 'react';
import { View } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import { Form } from '@redlink/components/organisms';
import { Space, withPopUp, Text } from '@redlink/components/atoms';
import {
  FormSection,
  TextInput,
  ColorInput,
  ThemeItemThumbnale,
  Checkbox,
  usePlan,
} from '@redlink/components/molecules';
import { DataEntryModalTemplate } from '@redlink/components/templates';
import { useLocale } from '@redlink/assets/locale';
import { useMutation, newThemeQuery, editThemeQuery } from '@redlink/services';
import { initialState, reducer, types } from './store';
import { freshCache } from './cache';

export const ThemeEditPage = withPopUp(
  memo(({ onDismiss, data }) => {
    const { styles } = useStyle();
    const { dictionary } = useLocale();
    const { showPlanDialog } = usePlan();

    const [
      {
        id,
        title,
        primaryColor,
        titleColor,
        subtitleColor,
        backgroundColor,
        cardBackgroundColor,
        saparatorColor,
        coverBackagroundColor,
        coverTextColor,
        descriptionColor,
        coverHasGradiant,
        isCreateByUser,
        isEditMode,
      },
      dispatch,
    ] = useReducer(reducer, initialState(data));

    const [submitTheme, { loading: submitThemeIsLoading }] = useMutation(
      !isEditMode ? newThemeQuery : editThemeQuery,
      {
        done: () => onDismiss(),
        update: freshCache({ isEditMode, id }),
        limited: () => {
          onDismiss && onDismiss();
          showPlanDialog();
        },
      }
    );

    return (
      <DataEntryModalTemplate previewContainerStyle={styles.previewContainer}>
        <Form
          isBusy={submitThemeIsLoading}
          actions={[
            {
              title: dictionary.cancel,
              variant: 'secondary',
              onPress: onDismiss,
            },
            {
              title: dictionary.add,
              variant: 'primary',
              disabled: !isCreateByUser,
              onPress: () =>
                submitTheme({
                  id,
                  title,
                  palette: {
                    primary: primaryColor,
                    title: titleColor,
                    subtitle: subtitleColor,
                    background: backgroundColor,
                    cardBackground: cardBackgroundColor,
                    saparator: saparatorColor,
                    coverBackaground: coverBackagroundColor,
                    coverText: coverTextColor,
                    coverHasGradiant: coverHasGradiant,
                    description: descriptionColor,
                  },
                }),
            },
          ]}
        >
          <FormSection title={dictionary.newTheme}>
            <TextInput
              require
              editable={isCreateByUser}
              title={dictionary.title}
              value={title}
              onChangeText={(value) =>
                dispatch({ type: types.SET_TITLE, payload: value })
              }
            />
            <Space size={24} />
            <Text style={styles.title}>
              {dictionary.primaryButtonColorsTitle}
            </Text>
            <Space size={4} />
            <View style={styles.columnContainer}>
              <ColorInput
                disabled={!isCreateByUser}
                style={styles.colorInput}
                title={dictionary.primaryColor}
                value={primaryColor}
                onChange={(value) =>
                  dispatch({ type: types.SET_PRIMARY_COLOR, payload: value })
                }
              />
              <Space size={16} />
              <View style={styles.emptyColumn} />
            </View>
            <Space size={24} />
            <Text style={styles.title}>{dictionary.backgroundColorsTitle}</Text>
            <Space size={4} />
            <View style={styles.columnContainer}>
              <ColorInput
                disabled={!isCreateByUser}
                style={styles.colorInput}
                title={dictionary.cardColor}
                value={cardBackgroundColor}
                onChange={(value) =>
                  dispatch({
                    type: types.SET_CARD_BACKGROUND_COLOR,
                    payload: value,
                  })
                }
              />
              <Space size={16} />
              <ColorInput
                disabled={!isCreateByUser}
                style={styles.colorInput}
                title={dictionary.saparatorColor}
                value={saparatorColor}
                onChange={(value) =>
                  dispatch({ type: types.SET_SAPARATOR_COLOR, payload: value })
                }
              />
            </View>
            <Space size={8} />
            <View style={styles.columnContainer}>
              <ColorInput
                disabled={!isCreateByUser}
                style={styles.colorInput}
                title={dictionary.backgroundColor}
                value={backgroundColor}
                onChange={(value) =>
                  dispatch({ type: types.SET_BACKGROUND_COLOR, payload: value })
                }
              />
              <Space size={16} />
              <View style={styles.emptyColumn} />
            </View>
            <Space size={24} />
            <Text style={styles.title}>{dictionary.coverColorsTitle}</Text>
            <Space size={4} />
            <View style={styles.columnContainer}>
              <ColorInput
                disabled={!isCreateByUser}
                style={styles.colorInput}
                title={dictionary.coverColor}
                value={coverBackagroundColor}
                onChange={(value) =>
                  dispatch({
                    type: types.SET_COVER_BACKGROUND_COLOR,
                    payload: value,
                  })
                }
              />
              <Space size={16} />
              <ColorInput
                disabled={!isCreateByUser}
                style={styles.colorInput}
                title={dictionary.coverTextColor}
                value={coverTextColor}
                onChange={(value) =>
                  dispatch({
                    type: types.SET_COVER_TEXT_COLOR,
                    payload: value,
                  })
                }
              />
            </View>
            <Space size={8} />
            <Checkbox
              title={dictionary.withGradiantOption}
              value={coverHasGradiant}
              onChange={(value) =>
                dispatch({ type: types.SET_COVER_HAS_GRADIANT, payload: value })
              }
            />
            <Space size={24} />
            <Text style={styles.title}>{dictionary.textColorsTitle}</Text>
            <Space size={4} />
            <View style={styles.columnContainer}>
              <ColorInput
                disabled={!isCreateByUser}
                style={styles.colorInput}
                title={dictionary.titleColor}
                value={titleColor}
                onChange={(value) =>
                  dispatch({ type: types.SET_TITLE_COLOR, payload: value })
                }
              />
              <Space size={16} />
              <ColorInput
                disabled={!isCreateByUser}
                style={styles.colorInput}
                title={dictionary.subtitleColor}
                value={subtitleColor}
                onChange={(value) =>
                  dispatch({ type: types.SET_SUBTITLE_COLOR, payload: value })
                }
              />
            </View>
            <Space size={8} />
            <View style={styles.columnContainer}>
              <ColorInput
                disabled={!isCreateByUser}
                style={styles.colorInput}
                title={dictionary.descriptionColor}
                value={descriptionColor}
                onChange={(value) =>
                  dispatch({
                    type: types.SET_DESCRIPTION_COLOR,
                    payload: value,
                  })
                }
              />
              <Space size={16} />
              <View style={styles.emptyColumn} />
            </View>
          </FormSection>
        </Form>
        <ThemeItemThumbnale
          palette={{
            primary: primaryColor,
            saparator: saparatorColor,
            title: titleColor,
            subtitle: subtitleColor,
            background: backgroundColor,
            cardBackground: cardBackgroundColor,
          }}
        />
      </DataEntryModalTemplate>
    );
  })
);

const useStyle = createUseStyle(({ colors, size }) => ({
  columnContainer: {
    flexDirection: 'row',
  },
  colorInput: {
    flex: 1,
  },
  previewContainer: {
    padding: 0,
  },
  emptyColumn: {
    flex: 1,
    paddingHorizontal: 6,
  },
  title: {
    fontSize: 14,
    color: colors.gray21,
  },
}));
